import { PageLayout } from '@/components/layout/PageLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ArrowLeftRight, Database, Layers, ArrowRight, CheckCircle, Clock, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { t } from '@/i18n';

const dataFlows = [
  { name: t('sap.productionOrders'), direction: 'SAP → App', frequency: t('sap.realTime'), lastSync: '2 min ago', status: 'ok' },
  { name: t('sap.inventoryLevels'), direction: 'SAP → App', frequency: t('sap.every5Min'), lastSync: '3 min ago', status: 'ok' },
  { name: t('sap.customerMaster'), direction: 'SAP → App', frequency: t('sap.daily'), lastSync: '06:00 today', status: 'ok' },
  { name: t('sap.dispatchConfirmations'), direction: 'App → SAP', frequency: t('sap.onEvent'), lastSync: '15 min ago', status: 'ok' },
  { name: t('sap.loadingPlans'), direction: 'App → SAP', frequency: t('sap.every15Min'), lastSync: '12 min ago', status: 'ok' },
  { name: t('sap.qualityResults'), direction: 'App → SAP', frequency: t('sap.onEvent'), lastSync: '1 hr ago', status: 'warning' },
  { name: t('sap.materialSpecs'), direction: 'SAP → App', frequency: t('sap.weekly'), lastSync: 'Mon 06:00', status: 'ok' },
  { name: t('sap.financialData'), direction: 'SAP → App', frequency: t('sap.daily'), lastSync: '06:00 today', status: 'ok' },
];

const syncModules = [
  { module: t('sap.ppProduction'), status: 'ok', records: '1,247' },
  { module: t('sap.mmMaterials'), status: 'ok', records: '856' },
  { module: t('sap.sdSales'), status: 'ok', records: '2,341' },
  { module: t('sap.qmQuality'), status: 'warning', records: '412' },
  { module: t('sap.pmMaintenance'), status: 'ok', records: '189' },
  { module: t('sap.fiFinance'), status: 'ok', records: '3,567' },
];

const apiLog = [
  { time: '13:52:14', method: 'POST', endpoint: '/api/dispatch/confirm', status: 200, duration: '142ms' },
  { time: '13:50:02', method: 'GET', endpoint: '/api/orders/sync', status: 200, duration: '89ms' },
  { time: '13:45:31', method: 'GET', endpoint: '/api/inventory/levels', status: 200, duration: '234ms' },
  { time: '13:40:18', method: 'POST', endpoint: '/api/loading/plan', status: 200, duration: '167ms' },
  { time: '13:35:44', method: 'GET', endpoint: '/api/production/orders', status: 200, duration: '312ms' },
  { time: '13:30:12', method: 'POST', endpoint: '/api/quality/results', status: 408, duration: '5001ms' },
  { time: '13:25:03', method: 'GET', endpoint: '/api/materials/specs', status: 200, duration: '78ms' },
  { time: '13:20:55', method: 'GET', endpoint: '/api/customers/master', status: 200, duration: '156ms' },
];

const StatusIcon = ({ status }) => {
  if (status === 'ok') return <CheckCircle className="h-4 w-4 text-green-500" />;
  if (status === 'warning') return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
  return <Clock className="h-4 w-4 text-gray-400" />;
};

export default function SAPIntegrace() {
  return (
    <PageLayout title={t('sap.title')} concept>
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold tracking-tight mb-2">{t('sap.heading')}</h2>
          <p className="text-muted-foreground">{t('sap.subtitle')}</p>
        </div>

        {/* Integration Diagram */}
        <Card className="p-8">
          <div className="flex items-center justify-center gap-6">
            {/* OMYA App */}
            <div className="w-56 min-h-[180px] bg-gradient-to-br from-primary/5 to-primary/10 rounded-2xl p-5 text-center border border-primary/20 flex flex-col items-center justify-center">
              <div className="w-14 h-14 mx-auto bg-gradient-to-br from-primary to-primary/80 rounded-xl flex items-center justify-center mb-3 shadow-glow-primary">
                <Layers className="h-7 w-7 text-white" />
              </div>
              <div className="font-bold">{t('sap.omyaDigitalPlant')}</div>
              <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
                <div>{t('sap.supplyChainTrucks')}</div>
                <div>{t('sap.planningOrders')}</div>
              </div>
            </div>

            {/* Arrow 1 */}
            <div className="flex flex-col items-center gap-1">
              <ArrowRight className="h-5 w-5 text-primary" />
              <div className="text-[9px] text-primary font-semibold">REST API</div>
              <ArrowRight className="h-5 w-5 text-accent rotate-180" />
            </div>

            {/* Integration Layer */}
            <div className="w-48 min-h-[180px] bg-gradient-to-br from-accent/5 to-accent/10 rounded-2xl p-5 text-center border-2 border-accent/30 flex flex-col items-center justify-center">
              <div className="w-14 h-14 mx-auto bg-gradient-to-br from-accent to-accent/80 rounded-xl flex items-center justify-center mb-3 shadow-glow-accent">
                <ArrowLeftRight className="h-7 w-7 text-white" />
              </div>
              <div className="font-bold">{t('sap.middleware')}</div>
              <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
                <div>{t('sap.dataMapping')}</div>
                <div>{t('sap.validationAuth')}</div>
              </div>
            </div>

            {/* Arrow 2 */}
            <div className="flex flex-col items-center gap-1">
              <ArrowRight className="h-5 w-5 text-primary" />
              <div className="text-[9px] text-primary font-semibold">RFC / OData</div>
              <ArrowRight className="h-5 w-5 text-accent rotate-180" />
            </div>

            {/* SAP */}
            <div className="w-56 min-h-[180px] bg-gradient-to-br from-[#0a67a3]/5 to-[#0a67a3]/10 rounded-2xl p-5 text-center border border-[#0a67a3]/20 flex flex-col items-center justify-center">
              <div className="w-14 h-14 mx-auto bg-gradient-to-br from-[#0a67a3] to-[#085a8e] rounded-xl flex items-center justify-center mb-3">
                <Database className="h-7 w-7 text-white" />
              </div>
              <div className="font-bold">SAP S/4HANA</div>
              <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
                <div>PP · MM · SD · QM</div>
                <div>PM · FI</div>
              </div>
            </div>
          </div>
        </Card>

        {/* Tabs: Data Flows / Sync Status / API Log */}
        <Tabs defaultValue="flows">
          <TabsList>
            <TabsTrigger value="flows">{t('sap.dataFlows')}</TabsTrigger>
            <TabsTrigger value="sync">{t('sap.syncStatus')}</TabsTrigger>
            <TabsTrigger value="log">{t('sap.apiLog')}</TabsTrigger>
          </TabsList>

          <TabsContent value="flows" className="mt-4">
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('sap.dataType')}</TableHead>
                    <TableHead>{t('sap.direction')}</TableHead>
                    <TableHead>{t('sap.frequency')}</TableHead>
                    <TableHead>{t('sap.lastSync')}</TableHead>
                    <TableHead>{t('sap.status')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dataFlows.map((flow, i) => (
                    <TableRow key={i} className="even:bg-muted/20">
                      <TableCell className="font-medium">{flow.name}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">{flow.direction}</Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{flow.frequency}</TableCell>
                      <TableCell className="text-sm">{flow.lastSync}</TableCell>
                      <TableCell><StatusIcon status={flow.status} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          <TabsContent value="sync" className="mt-4">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {syncModules.map((mod, i) => (
                <Card key={i} className={cn(
                  'p-4',
                  mod.status === 'warning' && 'border-yellow-300 bg-yellow-50/50',
                )}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold">{mod.module}</span>
                    <StatusIcon status={mod.status} />
                  </div>
                  <div className="text-xs text-muted-foreground">{mod.records} {t('sap.recordsSynced')}</div>
                  <div className="mt-2">
                    <Badge className={mod.status === 'ok' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                      {mod.status === 'ok' ? t('sap.synchronized') : t('sap.delayed')}
                    </Badge>
                  </div>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="log" className="mt-4">
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('sap.time')}</TableHead>
                    <TableHead>{t('sap.method')}</TableHead>
                    <TableHead>{t('sap.endpoint')}</TableHead>
                    <TableHead>{t('sap.status')}</TableHead>
                    <TableHead>{t('sap.duration')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {apiLog.map((entry, i) => (
                    <TableRow key={i} className="even:bg-muted/20">
                      <TableCell className="font-mono text-xs">{entry.time}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={cn(
                          'text-xs font-mono',
                          entry.method === 'POST' && 'bg-blue-50 text-blue-700',
                          entry.method === 'GET' && 'bg-green-50 text-green-700',
                        )}>
                          {entry.method}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-mono text-xs">{entry.endpoint}</TableCell>
                      <TableCell>
                        <Badge className={entry.status === 200 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                          {entry.status}
                        </Badge>
                      </TableCell>
                      <TableCell className={cn('text-xs', parseInt(entry.duration) > 1000 && 'text-red-600 font-semibold')}>
                        {entry.duration}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Info banner */}
        <Card className="bg-gradient-to-r from-yellow-50/80 to-amber-50/80 border-yellow-200/60 backdrop-blur-sm">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <Badge variant="outline" className="bg-yellow-100 text-yellow-800 border-yellow-300">INFO</Badge>
              <p className="text-sm text-yellow-900">
                <strong>{t('sap.infoTitle')}</strong> {t('sap.infoText')}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
